import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useRef } from "react";
import { cn } from "../lib/cn";
import { nextRadioIndex } from "./radio-navigation";

/**
 * Segmented control, design-system spec: 5-step likert with serif label,
 * mono index, teal-700 selected with shadow lift. Used in onboarding's
 * pulse-question step and any "where on this scale" prompt.
 */
export interface SegmentedOption<T extends string | number> {
  value: T;
  label: ReactNode;
  /** Optional mono index displayed above the label (01, 02, ...). */
  index?: string;
}

export interface SegmentedProps<T extends string | number> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T | null;
  onChange?: (next: T) => void;
  className?: string;
  ariaLabel?: string;
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  className,
  ariaLabel,
}: SegmentedProps<T>) {
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  // Roving tabindex: one tab stop (the checked option, or the first when none is), arrows move within.
  const activeIndex = options.findIndex((opt) => opt.value === value);
  const tabStopIndex = activeIndex >= 0 ? activeIndex : 0;

  function handleKeyDown(event: ReactKeyboardEvent, i: number) {
    const next = nextRadioIndex(event.key, i, options.length);
    if (next === null) return;
    event.preventDefault();
    const target = options[next];
    if (target) onChange?.(target.value);
    buttons.current[next]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn("grid gap-1.5", className)}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          // biome-ignore lint/a11y/useSemanticElements: native radio inputs can't accept the rich pad+border+stat block this design calls for; button+role+aria-checked is the spec'd accessible-name pattern
          <button
            key={String(opt.value)}
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={i === tabStopIndex ? 0 : -1}
            onClick={() => onChange?.(opt.value)}
            onKeyDown={(event) => handleKeyDown(event, i)}
            className={cn(
              // Active gets a 2px all-around teal-700 border (highlight
              // rule). Inactive keeps a 1px hair border with matching
              // padding so width doesn't jump on toggle. Min height 44px
              // hits WCAG 2.5.5 (touch target) so the likert column
              // doesn't fall short on mobile.
              "flex min-h-[44px] flex-col items-start gap-1 rounded-md text-left [transition:background-color_200ms_var(--ease-out-expo),border-color_200ms_var(--ease-out-expo),color_200ms_var(--ease-out-expo),box-shadow_200ms_var(--ease-out-expo)]",
              active
                ? "border-2 border-teal-700 bg-teal-700 px-[11px] py-[11px] text-white shadow-[0_8px_18px_-10px_color-mix(in_srgb,var(--color-teal-700)_55%,transparent)]"
                : "border border-hair bg-white/70 px-3 py-3 text-navy hover:border-mist-dark",
            )}
          >
            {opt.index ? (
              <span
                className={cn(
                  "font-mono text-[10px] uppercase tracking-[0.14em]",
                  active ? "text-white/70" : "text-gray-500",
                )}
              >
                {opt.index ?? `0${i + 1}`}
              </span>
            ) : null}
            {/*
             * Likert option label: Inter Tight (sans), 13px, weight 500.
             * Per current accessibility guidance (Section 508, WebAIM,
             * USWDS) small interactive UI text reads more reliably in
             * sans-serif for low-vision users; serifs blur at sub-14px
             * sizes on screens. The editorial question above still
             * uses Newsreader at 22px where serif legibility is fine.
             */}
            <span className="font-sans text-[13px] font-medium leading-snug tracking-[-0.005em]">
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
